"use client";

import findLast from "lodash/findLast";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { ProductNavigationItem } from "../product/V2/ProductPageNavigation";

type ProductNavigationOptions = {
    sections: ProductNavigationItem[];
    scrollMarginTop: number;
    scrollableContainerRef?: MutableRefObject<HTMLDivElement | null>;
};

export const useProductNavigation = ({
    sections,
    scrollMarginTop,
    scrollableContainerRef,
}: ProductNavigationOptions) => {
    const navigationItems = sections.filter((x) => x.isAvailable);

    const { lastVisibleSectionId, onSectionVisibilityChange } = useSectionVisibility(
        navigationItems,
        scrollMarginTop,
        scrollableContainerRef?.current
    );
    const [activeNavigationItem, setActiveNavigationItem] = useState<string | undefined>(lastVisibleSectionId);
    const suspendImplicitNavigationTimerRef = useRef<NodeJS.Timeout | null>(null);

    const onManualNavigationChange = useCallback((id: string) => {
        const element = document.getElementById(id);
        if (!element) return;

        if (suspendImplicitNavigationTimerRef.current) {
            clearTimeout(suspendImplicitNavigationTimerRef.current);
        }

        // Prevents the section visibilty changes to override the manual selection that just occured
        suspendImplicitNavigationTimerRef.current = setTimeout(() => {
            suspendImplicitNavigationTimerRef.current = null;
        }, 1500);

        setActiveNavigationItem(id);

        element.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    useEffect(() => {
        if (!suspendImplicitNavigationTimerRef.current && lastVisibleSectionId !== activeNavigationItem) {
            setActiveNavigationItem(lastVisibleSectionId);
        }
    }, [lastVisibleSectionId, activeNavigationItem]);

    useEffect(() => {
        // Handles the edge case when the top item is not being selected when scrolling up
        // This is related to the section visibility algorithm which covers most of the other cases
        const onScroll = () => {
            if (!!suspendImplicitNavigationTimerRef.current || !scrollMarginTop || window.scrollY > scrollMarginTop)
                return;
            const firstSectionId = navigationItems.length > 0 ? navigationItems[0].id : undefined;
            if (firstSectionId && firstSectionId !== activeNavigationItem) setActiveNavigationItem(firstSectionId);
        };
        window.addEventListener("scroll", onScroll, {
            passive: true,
        });
        return () => window.removeEventListener("scroll", onScroll);
    }, [navigationItems, activeNavigationItem, scrollMarginTop]);

    return { navigationItems, activeNavigationItem, onManualNavigationChange, onSectionVisibilityChange };
};

const useSectionVisibility = (
    sectionItems: ProductNavigationItem[],
    headerHeight: number,
    scrollableContainer?: HTMLDivElement | null
) => {
    const firstSectionId = sectionItems.length > 0 ? sectionItems[0].id : undefined;
    const initialSectionState = sectionItems.map(({ id }) => ({ id, isVisible: firstSectionId === id }));
    const [sections, setSections] = useState(initialSectionState);

    const onSectionVisibilityChange = (id: string, inView: boolean) => {
        setSections((sections) => sections.map((x) => ({ ...x, isVisible: x.id === id ? inView : x.isVisible })));
    };

    const lastVisibleSectionId = findLastVisibleSection(sections, headerHeight, scrollableContainer);

    return { onSectionVisibilityChange, lastVisibleSectionId };
};

const findLastVisibleSection = (
    sectionVisibility: { id: string; isVisible: boolean }[],
    headerHeight: number,
    scrollableContainer?: HTMLDivElement | null
) => {
    const firstNavigationItemId = sectionVisibility.length > 0 ? sectionVisibility[0].id : undefined;
    const lastVisibleItemId = findLast(sectionVisibility, (x) => x.isVisible)?.id;
    if (typeof window === "undefined") {
        return lastVisibleItemId;
    }
    if (scrollableContainer) {
        return scrollableContainer.scrollHeight <= headerHeight || !lastVisibleItemId
            ? firstNavigationItemId
            : lastVisibleItemId;
    } else {
        return window.scrollY <= headerHeight || !lastVisibleItemId ? firstNavigationItemId : lastVisibleItemId;
    }
};
